'use strict';
/*
  _common.js — generic kinetic-caption layout shared by all styles.

  layoutCue(ctx, st, cfg) renders the active cue (words staggering in with the
  cue's animation) AND the outgoing cue fading/sliding out during a transition,
  then calls cfg.paintToken(...) per word. Optional motion-blur trails on entry.

  cfg:
    sizes:      {big, mid, small}   base px per line "size"
    anchor:     'center' | number(0..1 of H)
    safeW:      fraction of W usable (default 0.9)
    stagger:    seconds between word reveals (default 0.045)
    reveal:     seconds for one word to fully appear (default 0.34)
    trans:      seconds for the outgoing cue to exit (default 0.24)
    trail:      motion-blur ghost count on entry (default 0 = off)
    family:     registered font family name (optional)
    paintToken: (ctx, {T, cx, cy, px, p, scl, rot, alpha, emph, anim, st}) => void
*/
const { clamp, easeOutBack, easeOutCubic } = require('../lib/draw');

function applyAnim(anim, p) {
  const eb = easeOutBack(p), ec = easeOutCubic(p);
  let tx = 0, ty = 0, scl = 1, rot = 0, alpha = clamp(p * 1.6, 0, 1);
  switch (anim) {
    case 'slam': scl = 1 + (1 - p) * 1.25; break;
    case 'drop': ty = -(1 - eb) * 80; break;
    case 'rise': ty = (1 - eb) * 90; break;
    case 'punch': tx = -(1 - p) * 60; break;
    case 'split': tx = (1 - p) * 56; rot = (1 - p) * 0.14; break;
    case 'pop': scl = 0.35 + ec * 0.65 + (p < 0.6 ? Math.sin(p * 5) * 0.06 : 0); break;
    case 'type': alpha = p > 0.2 ? 1 : 0; break;
    case 'wobble': rot = Math.sin(p * 7) * (1 - p) * 0.25; scl = 0.6 + ec * 0.4; break;
    case 'spring': scl = eb; ty = -(1 - eb) * 40; break;
    case 'flip': scl = Math.max(0.02, Math.abs(Math.cos((1 - p) * Math.PI * 0.5))); break; // vertical flip-in
    case 'zoom': scl = 0.2 + ec * 0.8; rot = (1 - p) * 0.2; break;
    case 'swing': rot = (1 - eb) * 0.5; ty = -(1 - eb) * 50; break;
    default: scl = 1 + (1 - p) * 0.4;
  }
  return { tx, ty, scl, rot, alpha };
}

function sizeKey(L) { return L.size || 'mid'; }

// draw one cue. phase: 'enter' uses per-token reveal; 'exit' uses a global fade-out.
function drawOneCue(ctx, st, cue, cfg, phase, exitE) {
  const { tk } = st;
  const sizes = cfg.sizes, fam = cfg.family;
  const safeW = (cfg.safeW || 0.9) * st.W;
  const stagger = cfg.stagger ?? 0.045;
  const reveal = cfg.reveal ?? 0.34;
  const trail = phase === 'enter' ? (cfg.trail || 0) : 0;
  const age = st.cueAge;

  let maxW = 0;
  for (const L of cue.lines) {
    const px = sizes[sizeKey(L)]; ctx.font = tk.font(px, 900, fam);
    const sp = px * 0.26; let w = -sp;
    for (const tkn of L.tokens) w += ctx.measureText(tkn.t).width + sp;
    if (w > maxW) maxW = w;
  }
  const sc = Math.min(1, safeW / Math.max(1, maxW));
  let totalH = 0; for (const L of cue.lines) totalH += sizes[sizeKey(L)] * sc * 1.08;
  const anchorY = (cfg.anchor === 'center' || cfg.anchor == null) ? st.CY : st.H * cfg.anchor;
  let y = anchorY - totalH / 2;

  // global exit transform
  const exitTy = phase === 'exit' ? -exitE * 46 : 0;
  const exitAlpha = phase === 'exit' ? (1 - exitE) : 1;
  const exitScl = phase === 'exit' ? (1 - exitE * 0.08) : 1;

  // backing pass: draw ALL line panels first, so a descender on one line is never
  // covered by the next line's panel (the text pass below paints on top).
  if (cfg.prePaintLine) {
    let py = y, pk = 0;
    for (const L of cue.lines) {
      const px = sizes[sizeKey(L)] * sc; ctx.font = tk.font(px, 900, fam);
      const sp = px * 0.26; let lw = -sp;
      for (const tkn of L.tokens) lw += ctx.measureText(tkn.t).width + sp;
      const lineCY = py + px * 1.08 / 2;
      const lp = phase === 'exit' ? 1 : clamp((age - pk * stagger + reveal * 0.5) / (reveal * 1.5), 0, 1);
      if (lp > 0) cfg.prePaintLine(ctx, { cx: st.CX, cy: lineCY, w: lw, px, alpha: exitAlpha * lp, st });
      py += px * 1.08; pk += L.tokens.length;
    }
  }

  const perLetter = !!cfg.perLetter;
  const lstag = cfg.letterStagger ?? 0.028;
  let k = 0;
  for (const L of cue.lines) {
    const px = sizes[sizeKey(L)] * sc; ctx.font = tk.font(px, 900, fam);
    const sp = px * 0.26; let lw = -sp;
    for (const tkn of L.tokens) lw += ctx.measureText(tkn.t).width + sp;
    let x = st.CX - lw / 2; const lineCY = y + px * 1.08 / 2;
    for (const tkn of L.tokens) {
      const T = tkn.t, tw = ctx.measureText(T).width;
      const wordStart = age - k * stagger;
      // per-letter funk (skip for the emphasised chip word so the box stays intact)
      if (perLetter && !tkn.emph) {
        let lx = x;
        const chars = Array.from(T);
        for (let ci = 0; ci < chars.length; ci++) {
          const ch = chars[ci], cw = ctx.measureText(ch).width;
          const p = phase === 'exit' ? 1 : clamp((wordStart - ci * lstag) / reveal, 0, 1);
          if (p > 0) {
            const a = applyAnim(cue.anim, p);
            const bob = Math.sin(ci * 0.8 + st.NOW / 320) * px * 0.03;
            cfg.paintToken(ctx, { T: ch, cx: lx + cw / 2 + a.tx, cy: lineCY + a.ty + exitTy + bob, px, p,
              scl: a.scl * exitScl, rot: a.rot, alpha: a.alpha * exitAlpha, emph: false, anim: cue.anim, tw: cw, st, ghost: false });
          }
          lx += cw;
        }
        x += tw + sp; k++; continue;
      }
      const p = phase === 'exit' ? 1 : clamp(wordStart / reveal, 0, 1);
      k++;
      if (p > 0) {
        for (let j = trail; j >= 1; j--) {
          if (p > 0.94) break;
          const pp = clamp(p - j * 0.07, 0, 1); if (pp <= 0) continue;
          const ga = applyAnim(cue.anim, pp);
          cfg.paintToken(ctx, { T, cx: x + tw / 2 + ga.tx, cy: lineCY + ga.ty + exitTy, px, p: pp,
            scl: ga.scl * exitScl, rot: ga.rot, alpha: ga.alpha * exitAlpha * 0.16 * (1 - j / (trail + 1)), emph: tkn.emph, anim: cue.anim, tw, st, ghost: true });
        }
        const a = applyAnim(cue.anim, p);
        cfg.paintToken(ctx, { T, cx: x + tw / 2 + a.tx, cy: lineCY + a.ty + exitTy, px, p,
          scl: a.scl * exitScl, rot: a.rot, alpha: a.alpha * exitAlpha, emph: tkn.emph, anim: cue.anim, tw, st, ghost: false });
      }
      x += tw + sp;
    }
    y += px * 1.08;
  }
}

function layoutCue(ctx, st, cfg) {
  const trans = cfg.trans ?? 0.24;
  // outgoing cue exit
  if (st.prevCue && st.exitAge >= 0 && st.exitAge < trans) {
    drawOneCue(ctx, st, st.prevCue, cfg, 'exit', clamp(st.exitAge / trans, 0, 1));
  }
  if (st.cue) drawOneCue(ctx, st, st.cue, cfg, 'enter', 0);
}

module.exports = { layoutCue, applyAnim };
